// @flow
import * as React from "react";

import { Card, DownloadWidget, Section, UploadWidget } from "buba-components";
import { USER_TYPE__GUARANTEE_AGENT } from "buba-components/src/utils/userType";
import { logError } from "buba-components/src/utils/loggers";
import { mapUnprocessableEntityError } from "buba-components/src/data/statusErrors";
import { allowedFileFormats } from "buba-components/src/utils/fileFormats";
import { splitFileRequestsAndFiles } from "buba-components/src/utils/files";
import { uploadAndCheckFileV2 } from "../../api/v2actions";

import styles from "./Documents.scss";

type UploadsProps = {
  downloadAction: ({ id: number }) => Promise<any>,
  fileRequests: Array<any>,
  inquiryProcessId: number,
  inquiryProcessUploadedFiles: Array<any>,
  onError: number => void,
  refetchFiles: () => Promise<any>
};

type UploadWidgetProps = {
  id: number,
  requestId?: number,
  uploadAction: (conf: Object) => Promise<any>
};

type UploadWidgetForGuaranteeAgentProps = {
  id: number,
  requestId?: number
};

const UploadsNew = ({
  downloadAction,
  fileRequests,
  inquiryProcessId,
  inquiryProcessUploadedFiles = [],
  onError,
  refetchFiles
}: UploadsProps) => {
  const uploadWidgetProps = ({ id, requestId, uploadAction }: UploadWidgetProps = {}) => {
    return {
      fileTypes: allowedFileFormats,
      initialStatus: "not_uploaded",
      name: "uploadForCustomer",
      title: "Dokument auswählen",
      selectedCallback: ({ file, markFailure, markSuccess, reset }) => {
        uploadAction({
          id,
          file,
          file_upload_request_id: requestId
        })
          .then(res => {
            const { status } = res;
            if (status === "rejected") {
              markFailure();
            } else {
              markSuccess();
              refetchFiles();
            }
            reset();
          })
          .catch(err => {
            const {
              status,
              data: { errors = [] }
            } = err.response;

            if (status === 422) {
              errors.forEach(e => markFailure(mapUnprocessableEntityError(e.type)));
            } else {
              onError(status);
            }
            reset();
            logError(err);
          });
      }
    };
  };

  const uploadWidgetForGuaranteeAgentProps = ({
    id,
    requestId
  }: UploadWidgetForGuaranteeAgentProps) => {
    return uploadWidgetProps({
      id,
      requestId,
      uploadAction: conf => {
        return uploadAndCheckFileV2(conf);
      }
    });
  };

  const uploadedFilesForGuaranteeAgent = inquiryProcessUploadedFiles.filter(
    ({ hiddenFor }) => hiddenFor !== USER_TYPE__GUARANTEE_AGENT
  );
  const { unfulfilledFileRequests, fulfilledFileRequests, additionalFiles } =
    splitFileRequestsAndFiles(fileRequests, uploadedFilesForGuaranteeAgent);

  return (
    <Section title="Upload-Bereich" numbered reducedMargin>
      <Card>
        {/* Documents for BUBA (Guarantee agent) */}
        <header>Unterlagen für die Bürgschaftsbank</header>
        <div className={styles.widgetContainer}>
          <UploadWidget
            {...uploadWidgetForGuaranteeAgentProps({ id: inquiryProcessId })}
            name="uploadForGuaranteeAgent"
          />
          {unfulfilledFileRequests.map(file => (
            <UploadWidget
              {...uploadWidgetForGuaranteeAgentProps({
                id: inquiryProcessId,
                requestId: file.id
              })}
              title={file.description || file.fileName}
              key={file.id}
              name="uploadForGuaranteeAgent"
            />
          ))}
          {fulfilledFileRequests.map(req => (
            <DownloadWidget
              uploadDate={req.uploadedFile?.createdAt}
              key={req.id}
              id={Number(req.uploadedFile?.id)}
              title={req.description || req.uploadedFile?.fileName}
              status={req.uploadedFile?.downloadedByGuaranteeAgentAt ? "downloaded" : "uploaded"}
              downloadAction={downloadAction}
              filename={req.uploadedFile?.fileName}
            />
          ))}
          {additionalFiles.map(file => (
            <DownloadWidget
              uploadDate={file.createdAt}
              key={file.id}
              id={Number(file.id)}
              title={file.fileName}
              status={file.downloadedByGuaranteeAgentAt ? "downloaded" : "uploaded"}
              downloadAction={downloadAction}
              filename={file.fileName}
            />
          ))}
        </div>
      </Card>
    </Section>
  );
};

export default UploadsNew;
